<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('paymentTools.configureSubtitle') }}</span>
      </div>
      <div class="ov-header__actions">
        <button
          type="button"
          class="ov-btn ov-btn--primary"
          :disabled="paymentMethodList.length === 0"
          @click="apiCreatePaymentMethod"
        >
          <v-icon small>mdi-content-save-outline</v-icon>
          {{ $t('paymentTools.saveMethods') }}
        </button>
      </div>
    </div>

    <div class="ov-grid">
      <!-- ===== LISTE DES MOYENS AJOUTÉS ===== -->
      <div class="ov-stack">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__title">
                À enregistrer
                <span class="ov-count">{{ paymentMethodList.length }}</span>
              </div>
              <div class="ov-card__sub">{{ $t('paymentTools.noMethodsSub') }}</div>
            </div>
            <div v-if="paymentMethodList.length > 0" class="ov-search">
              <v-icon small>mdi-magnify</v-icon>
              <input v-model="search" type="text" class="ov-input" :placeholder="$t('paymentTools.searchPlaceholder')" />
            </div>
          </div>

          <v-data-table
            v-if="paymentMethodList.length > 0"
            :headers="headers"
            :items="paymentMethodList"
            :search="search"
            class="ov-table"
            hide-default-footer
            :items-per-page="-1"
          >
            <template #[`item.designation`]="{ item }">
              <span class="pt-designation">{{ item.designation }}</span>
            </template>
            <template #[`item.typeModePaiementDesignation`]="{ item }">
              <span class="ov-badge ov-badge--navy ov-badge--plain">{{ item.typeModePaiementDesignation }}</span>
            </template>
            <template #[`item.telephoneGenerique`]="{ item }">
              <span v-if="item.telephoneGenerique">+225 {{ item.telephoneGenerique }}</span>
              <span v-else class="pt-dash">—</span>
            </template>
            <template #[`item.actions`]="{ item }">
              <div class="ov-row-actions pt-actions">
                <button
                  type="button"
                  class="ov-icon-btn"
                  :title="$t('paymentTools.editMethod')"
                  @click="editPayment(paymentMethodList.indexOf(item))"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </button>
                <button
                  type="button"
                  class="ov-icon-btn ov-icon-btn--danger"
                  :title="$t('paymentTools.deleteMethod')"
                  @click="deletePaymentMethod(paymentMethodList.indexOf(item))"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </button>
              </div>
            </template>
          </v-data-table>

          <div v-else class="ov-card__body">
            <div class="ov-empty">
              <v-icon size="40" color="#cbd5e1">mdi-wallet-plus-outline</v-icon>
              <div class="pt-empty__title">{{ $t('paymentTools.noMethods') }}</div>
              <div>{{ $t('paymentTools.noMethodsSub') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== FORMULAIRE ===== -->
      <aside class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">
                {{ editingIndex >= 0 ? $t('paymentTools.editPaymentMethod') : $t('paymentTools.addPaymentMethod') }}
              </div>
              <div class="ov-card__sub">{{ $t('paymentTools.formSubtitle') }}</div>
            </div>
          </div>

          <div class="ov-card__body pt-form">
            <div v-if="$store.state.userAuthentified.roleCode == 'RoleAdminSocieteMere'" class="ov-field">
              <label class="ov-label">{{ $t('paymentTools.transportCompany') }}<span class="ov-required">*</span></label>
              <select v-model="compagnieTransportRaisonSociale" class="ov-select">
                <option :value="null" disabled>{{ $t('paymentTools.selectCompany') }}</option>
                <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
                  {{ c.raisonSociale }}
                </option>
              </select>
            </div>

            <div class="ov-field">
              <label class="ov-label">{{ $t('paymentTools.methodName') }}<span class="ov-required">*</span></label>
              <input
                v-model="modePaiementModel.designation"
                type="text"
                class="ov-input"
                :placeholder="$t('paymentTools.methodPlaceholder')"
              />
            </div>

            <div class="ov-field">
              <label class="ov-label">{{ $t('paymentTools.operator') }}<span class="ov-required">*</span></label>
              <select v-model="modePaiementModel.typeModePaiementDesignation" class="ov-select">
                <option :value="null" disabled>{{ $t('paymentTools.selectOperator') }}</option>
                <option v-for="r in referenceTypePaiementList" :key="r.designation" :value="r.designation">
                  {{ r.designation }}
                </option>
              </select>
            </div>

            <div v-if="modePaiementModel.typeModePaiementDesignation !== 'ModePaiementEnEspece'" class="ov-field">
              <label class="ov-label">{{ $t('paymentTools.accountNumber') }}<span class="ov-required">*</span></label>
              <div class="ov-input-group">
                <span class="ov-input-prefix">+225</span>
                <input
                  v-model="modePaiementModel.telephoneGenerique"
                  type="text"
                  class="ov-input"
                  :placeholder="$t('paymentTools.accountPlaceholder')"
                />
              </div>
            </div>
          </div>

          <div class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--secondary ov-btn--sm" @click="reininitializeFormNewMode">
              <v-icon small>mdi-close</v-icon>
              {{ $t('paymentTools.cancel') }}
            </button>
            <button type="button" class="ov-btn ov-btn--navy ov-btn--sm" @click="addNewPayment(modePaiementModel)">
              <v-icon small>{{ editingIndex >= 0 ? 'mdi-check' : 'mdi-plus' }}</v-icon>
              {{ editingIndex >= 0 ? $t('paymentTools.updateMethod') : $t('paymentTools.addToList') }}
            </button>
          </div>
        </div>
      </aside>
    </div>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  API_CREER_MODE_PAIEMENT,
  HEADERS,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";

export default {
  name: "CreerModePaiement",

  data() {
    return {
      errorMsg: null,
      successMsg: null,
      overlay: null,
      search: "",
      editingIndex: -1,

      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,

      referenceTypePaiementList: [],
      paymentMethodList: [],

      modePaiementModel: {
        designation: null,
        telephoneGenerique: null,
        typeModePaiementDesignation: null,
        compagnieTransportRaisonSociale: null,
      },

      referenceModePaiement: {
        referenceFamilleDesignation: "referenceFamilleModePaiement",
      },

      objectToSendReferenceModePaiement: {
        datas: [],
      },

      modePaiementToSend: {
        datas: [],
      },

      options: {
        index: 0,
        size: 8,
      },
    };
  },

  methods: {
    // OBTENIR LA LISTE DES DEMANDES D'ADHESIONS VALIDÉES
    async getCompagnieTransportList() {
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
      ) {
        axios
          .post(
            API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
            this.options,
            {
              headers: HEADERS(this.$store.state.userAuthentified.token),
            }
          )
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code != 800) {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              } else {
                this.compagnieTransportList = response.data.items;
              }
            } else {
              this.errorMsg = "Erreur lors de la récupération des données";
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    // API CREATE NEW PAYMENT MODE
    async apiCreatePaymentMethod() {
      this.overlay = true;
      this.paymentMethodList.forEach((element) => {
        this.modePaiementToSend.datas.push(element);
      });
      await axios
        .post(API_CREER_MODE_PAIEMENT, this.modePaiementToSend, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.successMsg = response.data.status.message;
            showSuccessMessage();
            this.modePaiementToSend.datas = [];
            // La liste n'est vidée qu'une fois enregistrée : en cas d'échec, on peut réessayer
            this.paymentMethodList = [];
          } else {
            this.errorMsg = response.data.status.message;
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.modePaiementToSend.datas = [];
          this.errorMsg = e;
          console.error("Erreur lors de l'enregistrement du moyen de paiement", e);
          showErrorMessage();
        })
        .finally(() => {
          this.modePaiementToSend.datas = [];
          this.overlay = false;
        });
    },

    // REINITIALISER LE FORMULAIRE DE CRÉATION DES MODES DE PAIEMENT
    reininitializeFormNewMode() {
      this.modePaiementModel.designation = null;
      this.modePaiementModel.telephoneGenerique = null;
      this.modePaiementModel.typeModePaiementDesignation = null;
    },

    // AJOUTER / METTRE À JOUR UN MOYEN DE PAIEMENT DANS LA LISTE
    addNewPayment(payment) {
      // Vérification de base
      if (!this.modePaiementModel.designation || this.modePaiementModel.designation === "" ||
          !this.modePaiementModel.typeModePaiementDesignation || this.modePaiementModel.typeModePaiementDesignation === "") {
        this.errorMsg = this.$t('paymentTools.requiredFieldsError');
        showErrorMessage();
        return;
      }

      // Vérification du téléphone uniquement si ce n'est pas un paiement en espèce
      if (this.modePaiementModel.typeModePaiementDesignation !== 'ModePaiementEnEspece') {
        if (!this.modePaiementModel.telephoneGenerique || this.modePaiementModel.telephoneGenerique === "") {
          this.errorMsg = this.$t('paymentTools.requiredFieldsError');
          showErrorMessage();
          return;
        }
      }

      const paymentAdded = {
        designation: payment.designation,
        telephoneGenerique: payment.telephoneGenerique,
        typeModePaiementDesignation: payment.typeModePaiementDesignation,
        compagnieTransportRaisonSociale:
          this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
            ? this.compagnieTransportRaisonSociale
            : this.$store.state.userAuthentified.compagnieTransportRaisonSociale,
      };

      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere" &&
        (!this.compagnieTransportRaisonSociale || this.compagnieTransportRaisonSociale === "")
      ) {
        this.errorMsg = this.$t('paymentTools.selectCompany');
        showErrorMessage();
        return;
      }

      if (this.editingIndex >= 0) {
        this.$set(this.paymentMethodList, this.editingIndex, paymentAdded);
        this.editingIndex = -1;
      } else {
        this.paymentMethodList.push(paymentAdded);
      }

      this.compagnieTransportRaisonSociale = null;
      this.reininitializeFormNewMode();
    },

    editPayment(index) {
      const p = this.paymentMethodList[index];
      this.modePaiementModel.designation = p.designation;
      this.modePaiementModel.telephoneGenerique = p.telephoneGenerique;
      this.modePaiementModel.typeModePaiementDesignation = p.typeModePaiementDesignation;
      this.compagnieTransportRaisonSociale = p.compagnieTransportRaisonSociale;
      this.editingIndex = index;
    },

    //DELETE A REFERENCE
    deletePaymentMethod(position) {
      if (position < 0) return;
      this.paymentMethodList.splice(position, 1);
      // Garder l'édition en cours cohérente avec la liste
      if (this.editingIndex === position) {
        this.reininitializeFormNewMode();
        this.editingIndex = -1;
      } else if (this.editingIndex > position) {
        this.editingIndex -= 1;
      }
    },

    //OBTENIR REFERENCE DESIGNATION MODE DE PAIEMENT
    async obtenirReferenceModePaiementList() {
      this.objectToSendReferenceModePaiement.datas.push(
        this.referenceModePaiement
      );
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceModePaiement,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referenceTypePaiementList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },
  },

  watch: {
    'modePaiementModel.typeModePaiementDesignation'(newValue) {
      // Réinitialiser le téléphone si c'est un paiement en espèce
      if (newValue === 'ModePaiementEnEspece') {
        this.modePaiementModel.telephoneGenerique = null;
      }
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('paymentTools.designation'), value: 'designation', align: 'start' },
        { text: this.$t('paymentTools.operator'), value: 'typeModePaiementDesignation', align: 'start' },
        { text: this.$t('paymentTools.phoneNumber'), value: 'telephoneGenerique', align: 'start' },
        { text: this.$t('paymentTools.actions'), value: 'actions', sortable: false, align: 'center' },
      ];
    },
  },

  mounted() {
    this.obtenirReferenceModePaiementList();
    this.getCompagnieTransportList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.pt-designation {
  font-weight: 700;
  color: $ov-navy;
}

.pt-dash {
  color: $ov-subtle;
}

.pt-actions {
  justify-content: center;
}

.pt-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.ov-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.pt-empty__title {
  font-weight: 700;
  color: $ov-muted;
}
</style>
